import {
    Button,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    Heading,
    type OverlayTriggerState,
    useDialogAction,
} from '@gitbook/spine-react';
import type { LoadingStateHookResult } from '@gitbook/util-react-hooks';

/**
 * Button to update or merge a change request that is out of date.
 */
export function ChangeRequestOutOfDateButton(props: {
    onUpdate: () => Promise<void>;
    onMerge: () => Promise<void>;
    disabled: boolean;
    loadable: LoadingStateHookResult;
    dialogTrigger: OverlayTriggerState;
    title?: string | null;
}) {
    const {
        onUpdate,
        onMerge,
        dialogTrigger,
        disabled,
        loadable,
        title = "This change request is out of date with the space's primary content.\nUpdate to bring the latest edits into this change request.",
    } = props;

    return (
        <>
            <Button
                title={title ?? undefined}
                kind="secondary"
                size="medium"
                onPress={onUpdate}
                label="Update"
                className="max-md:hidden"
                loadable={loadable}
                disabled={disabled}
            />
            <DialogRoot {...dialogTrigger.rootProps}>
                <ConfirmOutOfDateMergeDialog onUpdate={onUpdate} onMerge={onMerge} />
            </DialogRoot>
        </>
    );
}

function ConfirmOutOfDateMergeDialog(props: {
    onUpdate: () => Promise<void>;
    onMerge: () => Promise<void>;
}) {
    const { onUpdate, onMerge } = props;
    const [isPending, startDialogAction] = useDialogAction();
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Out of date change request</Heading>
            </DialogHeader>
            <DialogBody>
                This change request is out of date with the space’s primary content. Do you want to
                update it with the latest changes before merging?
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button
                    label="Merge anyway"
                    kind="secondary"
                    disabled={isPending}
                    onPress={async () => {
                        await startDialogAction(async ({ close }) => {
                            await onMerge();
                            close();
                        });
                    }}
                />
                <Button
                    label="Update"
                    disabled={isPending}
                    onPress={async () => {
                        await startDialogAction(async ({ close }) => {
                            await onUpdate();
                            close();
                        });
                    }}
                />
            </DialogFooter>
        </Dialog>
    );
}
